// The chunk uploader of one meeting take (BUILD 1 WP-4, C-10).
//
// Frames stay in the device spool until the server acknowledges the chunk
// that holds them; only then is the range deleted. A chunk is read back from
// the spool, encoded as PCM1 and PUT with its sha256. At most three PUTs are
// in flight; a failure backs off 1, 2, 4 … 30 s with jitter and retries the
// same bytes (the server treats identical bytes as a no-op). The server
// ledger is authoritative: a 409 for a closed capture or the length limit
// stops the take's uploads, and a malformed chunk (a client bug) is dropped
// rather than retried forever. Owner: WP-4.
import type { PcmFrame, SpoolMeta, SpoolSession } from '@/speech/contracts';
import { chunkSeqRange, encodePcm1, type ChunkFrame } from './meeting-capture-chunks';
import type { MeetingChunkAck } from './meetings-api';

export const MAX_IN_FLIGHT = 3;
export const BACKOFF_MIN_MS = 1000;
export const BACKOFF_MAX_MS = 30_000;

export type UploadFailure = 'captureClosed' | 'limitReached';

export type UploaderDeps = {
  putChunk(
    meetingId: string,
    take: number,
    chunk: number,
    bytes: Uint8Array,
    sha256: string,
  ): Promise<MeetingChunkAck>;
  sha256(bytes: Uint8Array): Promise<string>;
  sleep(ms: number): Promise<void>;
  random(): number;
};

export type UploaderEvents = {
  onAck?(ack: { chunk: number; samples: number; stopRequested: boolean }): void;
  /** Chunks queued or in flight changed. */
  onPending?(pending: number): void;
  onOnline?(online: boolean): void;
  onFatal?(failure: UploadFailure): void;
  onDropped?(chunk: number): void;
};

type ChunkError = { status?: number; code?: string; retryAfterSec?: number | null };

const errorOf = (error: unknown): ChunkError =>
  typeof error === 'object' && error !== null ? (error as ChunkError) : {};

/** How to react to one failed PUT. Exported for tests. */
export function classifyChunkError(
  error: unknown,
): 'fatal-closed' | 'fatal-limit' | 'drop' | 'wait' | 'offline' {
  const { status, code } = errorOf(error);
  if (status === 409 && code === 'meeting_capture_limit_reached') return 'fatal-limit';
  if (status === 404 || (status === 409 && code === 'meeting_capture_closed'))
    return 'fatal-closed';
  if (status === 400 || status === 413 || status === 415) return 'drop';
  if (status === undefined || status === 0) return 'offline';
  return 'wait';
}

export function backoffMs(attempt: number, random: () => number, retryAfterSec?: number | null) {
  if (retryAfterSec && retryAfterSec > 0) return retryAfterSec * 1000;
  const base = Math.min(BACKOFF_MAX_MS, BACKOFF_MIN_MS * 2 ** attempt);
  return Math.round(base * (0.5 + random() / 2));
}

const exactPcm = (frame: PcmFrame): ArrayBuffer =>
  frame.pcm.byteLength === frame.samples * 2 ? frame.pcm : frame.pcm.slice(0, frame.samples * 2);

async function framesOf(session: SpoolSession, chunk: number): Promise<ChunkFrame[]> {
  const [first, last] = chunkSeqRange(chunk);
  const frames: ChunkFrame[] = [];
  for await (const frame of session.frames(first, last))
    frames.push({ seq: frame.seq, pcm: exactPcm(frame) });
  return frames;
}

async function rememberChunk(
  session: SpoolSession,
  chunk: number,
  identity: NonNullable<SpoolMeta['meetingChunks']>[number],
) {
  const meta = session.meta();
  const [fromSeq, toSeq] = chunkSeqRange(chunk);
  try {
    await session.setState(
      meta.state,
      { meetingChunks: { ...meta.meetingChunks, [chunk]: identity } },
      { fromSeq, toSeq },
    );
  } catch {
    // Keep the take available for Try again; never send an identity whose
    // device checkpoint silently failed, or erase bytes after such an ack.
    throw Object.assign(new Error('meeting_chunk_checkpoint_failed'), { status: 400 });
  }
}

export type ChunkUploader = ReturnType<typeof createChunkUploader>;

/**
 * One take's upload queue. `enqueue` is idempotent per chunk; `drain`
 * resolves once nothing is queued or in flight (or the take was closed).
 */
export function createChunkUploader(input: {
  meetingId: string;
  take: number;
  session: SpoolSession;
  deps: UploaderDeps;
  events?: UploaderEvents;
}) {
  const { meetingId, take, session, deps, events = {} } = input;
  const queue: Array<{ chunk: number; final: boolean }> = [];
  const known = new Set<number>();
  const acked = new Set<number>();
  let inFlight = 0;
  let stopped = false;
  let online = true;
  let idle: Array<() => void> = [];

  const settleIdle = () => {
    if (inFlight > 0 || (queue.length > 0 && !stopped)) return;
    const waiting = idle;
    idle = [];
    for (const resolve of waiting) resolve();
  };
  const setOnline = (value: boolean) => {
    if (online === value) return;
    online = value;
    events.onOnline?.(value);
  };

  async function attempt(chunk: number, final: boolean): Promise<'done' | 'retry' | 'stop'> {
    const frames = await framesOf(session, chunk);
    if (!frames.length) return 'done';
    const original = session.meta().meetingChunks?.[chunk];
    const encoded = encodePcm1({
      take,
      chunkIndex: chunk,
      final: original?.final ?? final,
      frames,
    });
    const digest = await deps.sha256(encoded.bytes);
    const samples = frames.reduce((sum, frame) => sum + frame.pcm.byteLength / 2, 0);
    if (original && (original.sha256 !== digest || original.samples !== samples))
      throw Object.assign(new Error('meeting_chunk_replay_mismatch'), { status: 400 });
    const identity = { final: encoded.final, sha256: digest, samples };
    await rememberChunk(session, chunk, {
      ...identity,
      ...(original?.acknowledged ? { acknowledged: true } : {}),
    });
    const ack = await deps.putChunk(meetingId, take, chunk, encoded.bytes, digest);
    if (
      ack.take !== take ||
      ack.chunk !== chunk ||
      ack.frames !== frames.length ||
      ack.samples !== samples
    )
      throw Object.assign(new Error('meeting_chunk_ack_mismatch'), { status: 502 });
    const [first, last] = chunkSeqRange(chunk);
    await rememberChunk(session, chunk, { ...identity, acknowledged: true });
    await session.deleteRange(first, last);
    acked.add(chunk);
    setOnline(true);
    events.onAck?.({ chunk, samples: ack.samples, stopRequested: ack.stopRequested });
    return 'done';
  }

  async function failed(chunk: number, error: unknown, tries: number): Promise<boolean> {
    const verdict = classifyChunkError(error);
    if (verdict === 'fatal-closed' || verdict === 'fatal-limit') {
      stopped = true;
      events.onFatal?.(verdict === 'fatal-limit' ? 'limitReached' : 'captureClosed');
      return false;
    }
    if (verdict === 'drop') {
      events.onDropped?.(chunk);
      return false;
    }
    if (verdict === 'offline') setOnline(false);
    await deps.sleep(backoffMs(tries, deps.random, errorOf(error).retryAfterSec));
    return !stopped;
  }

  async function run(item: { chunk: number; final: boolean }) {
    for (let tries = 0; !stopped; tries += 1) {
      try {
        await attempt(item.chunk, item.final);
        return;
      } catch (error) {
        if (!(await failed(item.chunk, error, tries))) return;
      }
    }
  }

  function pump() {
    while (!stopped && inFlight < MAX_IN_FLIGHT && queue.length > 0) {
      const item = queue.shift()!;
      inFlight += 1;
      void run(item).finally(() => {
        inFlight -= 1;
        pump();
        events.onPending?.(queue.length + inFlight);
        settleIdle();
      });
    }
    settleIdle();
  }

  return {
    take,
    /** Queue chunk `chunk`; `final` marks the last chunk of an ended take. */
    enqueue(chunk: number, final = false) {
      if (stopped || known.has(chunk)) return;
      known.add(chunk);
      queue.push({ chunk, final });
      queue.sort((a, b) => a.chunk - b.chunk);
      pump();
      events.onPending?.(queue.length + inFlight);
    },
    has: (chunk: number) => known.has(chunk),
    acked: (chunk: number) => acked.has(chunk),
    /** Chunks queued or in flight. */
    pending: () => queue.length + inFlight,
    online: () => online,
    closed: () => stopped,
    /** An idle queue is insufficient: every queued chunk needs a matching acknowledgement. */
    complete: () => !stopped && queue.length === 0 && inFlight === 0 && known.size === acked.size,
    drain(): Promise<void> {
      if (inFlight === 0 && (queue.length === 0 || stopped)) return Promise.resolve();
      return new Promise((resolve) => idle.push(resolve));
    },
    /** Stop uploading (discard, or the meeting closed elsewhere). */
    stop() {
      stopped = true;
      queue.length = 0;
      events.onPending?.(inFlight);
      settleIdle();
    },
  };
}
